import type { ReactNode } from 'react';
import type { PosterInk, PosterState } from './helpers';
import { P, oswald, pressable } from './theme';

// -- The cartaz: cover image when the admin sent one, else the tagline in
//    Oswald caps on a coloured background. Same 3:4 sheet everywhere: the
//    wall, the product page cover and the confirmation screen. --

const INK_LOOK: Record<PosterInk, { bg: string; text: string; border?: string }> = {
  red: { bg: P.maxRed, text: P.white },
  pink: { bg: P.charm, text: P.black },
  black: { bg: P.black, text: P.white },
  cream: { bg: P.white, text: P.black, border: `1px solid ${P.lightBorder}` },
};

export type StampTone = 'green' | 'grey' | 'gold';
const STAMP_COLORS: Record<StampTone, string> = {
  green: P.green,
  grey: P.warmGray,
  gold: P.gold,
};

/** The rubber stamp slanted across a poster ("Liberada", "Expirada", "Aguardando liberação"). */
export function Stamp({
  label,
  tone,
  dashed = false,
  size = 13,
  style,
}: {
  label: string;
  tone: StampTone;
  dashed?: boolean;
  size?: number;
  style?: React.CSSProperties;
}) {
  const color = STAMP_COLORS[tone];
  return (
    <div
      style={{
        position: 'absolute',
        right: -6,
        top: '38%',
        transform: 'rotate(-12deg)',
        border: `3px ${dashed ? 'dashed' : 'solid'} ${color}`,
        color,
        fontFamily: oswald,
        fontWeight: 700,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        padding: '2px 10px',
        fontSize: size,
        lineHeight: 1.4,
        background: 'rgba(255,255,255,0.8)',
        zIndex: 2,
        pointerEvents: 'none',
        ...style,
      }}
    >
      {label}
    </div>
  );
}

export type PillTone = 'new' | 'ok' | 'exp' | 'wait' | 'lvl';
const PILL_LOOK: Record<PillTone, React.CSSProperties> = {
  new: { background: '#FDE1E4', color: P.maxRed, borderColor: `${P.maxRed}55` },
  ok: { background: P.greenSoft, color: P.green, borderColor: `${P.green}44` },
  exp: { background: '#EEE6E4', color: P.warmGray, borderColor: 'transparent' },
  wait: { background: P.goldSoft, color: P.gold, borderColor: `${P.gold}66` },
  lvl: { background: P.black, color: P.white, borderColor: 'transparent', borderRadius: 2, fontSize: 11 },
};

/** Small uppercase status pill (top-right corner of a poster). */
export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontFamily: oswald,
        fontWeight: 600,
        fontSize: 10,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        padding: '3px 9px',
        borderRadius: 999,
        border: '1px solid transparent',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        ...PILL_LOOK[tone],
      }}
    >
      {children}
    </span>
  );
}

/** The slightly tilted white price sticker at the poster's foot. */
export function PriceSticker({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontFamily: oswald,
        fontWeight: 700,
        fontSize: 15,
        background: P.white,
        color: P.black,
        padding: '2px 8px',
        borderRadius: 2,
        transform: 'rotate(3deg)',
        display: 'inline-block',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/** The call to action at a poster's foot, sized like the mock's small button:
 *  "Continuar" is a filled black button, "Renovar" (expired) an outlined one. */
export function PosterCta({ children, ghost = false }: { children: ReactNode; ghost?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: oswald,
        fontWeight: 600,
        fontSize: 12,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        padding: '8px 14px',
        borderRadius: 4,
        border: `2px solid ${P.black}`,
        background: ghost ? 'transparent' : P.black,
        color: ghost ? P.black : P.white,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

function Tape() {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: '50%',
        top: -8,
        width: 64,
        height: 20,
        background: 'rgba(255,243,214,0.8)',
        transform: 'translateX(-50%) rotate(-2deg)',
        boxShadow: '0 1px 2px rgba(0,0,0,.15)',
        zIndex: 3,
      }}
    />
  );
}

export function Poster({
  ink,
  coverImageUrl,
  headline,
  topLeft,
  topRight,
  footLeft,
  footRight,
  stamp,
  state = 'new',
  onOpen,
  ariaLabel,
  headlineSize = 22,
  style,
}: {
  ink: PosterInk;
  coverImageUrl?: string | null;
  headline: string;
  topLeft?: string;
  topRight?: ReactNode;
  footLeft?: string;
  footRight?: ReactNode;
  stamp?: { label: string; tone: StampTone; dashed?: boolean };
  state?: PosterState;
  onOpen?: () => void;
  ariaLabel?: string;
  headlineSize?: number;
  style?: React.CSSProperties;
}) {
  const look = INK_LOOK[ink];
  const hasCover = !!coverImageUrl;
  // Text sits on a dark scrim when there is a photo underneath.
  const text = hasCover ? P.white : look.text;
  const classes = [
    'products-poster',
    onOpen ? 'is-pressable' : '',
    state === 'owned' ? 'is-owned' : '',
    state === 'pending' ? 'is-pending' : '',
    state === 'expired' ? 'is-expired' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      {...(onOpen ? pressable(onOpen) : {})}
      className={classes}
      aria-label={ariaLabel}
      style={{
        position: 'relative',
        aspectRatio: '3 / 4',
        borderRadius: 3,
        padding: '16px 14px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxShadow: '0 10px 24px rgba(45,26,31,.16)',
        cursor: onOpen ? 'pointer' : 'default',
        overflow: 'hidden',
        textAlign: 'left',
        width: '100%',
        background: hasCover ? P.black : look.bg,
        color: text,
        border: hasCover ? undefined : look.border,
        ...style,
      }}
    >
      {hasCover ? (
        <>
          <img
            src={coverImageUrl ?? undefined}
            alt=""
            loading="lazy"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 55%, rgba(0,0,0,.7) 100%)',
              pointerEvents: 'none',
            }}
          />
        </>
      ) : ink === 'cream' ? null : (
        // The mock's ink posters carry a soft shadow + highlight; the white
        // (cream) sheet stays plain so it doesn't look smudged.
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(160deg, rgba(0,0,0,0) 40%, rgba(0,0,0,.33)), radial-gradient(circle at 70% 30%, rgba(255,255,255,.2), transparent 45%)',
            pointerEvents: 'none',
          }}
        />
      )}
      <Tape />
      {stamp && <Stamp label={stamp.label} tone={stamp.tone} dashed={stamp.dashed} />}

      <div
        style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 6,
          fontFamily: oswald,
          fontSize: 10,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          opacity: 0.85,
          minWidth: 0,
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{topLeft}</span>
        {topRight}
      </div>

      {/* With a photo the picture IS the poster; the headline only shows on the ink versions. */}
      {!hasCover && (
        <div
          style={{
            position: 'relative',
            fontFamily: oswald,
            fontWeight: 700,
            fontSize: headlineSize,
            lineHeight: 1.05,
            textTransform: 'uppercase',
            letterSpacing: '0.01em',
            margin: '10px 0',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 6,
            WebkitBoxOrient: 'vertical',
            overflowWrap: 'anywhere',
          }}
        >
          {headline}
        </div>
      )}

      <div
        style={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 8,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontFamily: oswald,
            fontSize: 10,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            opacity: 0.85,
            flex: '1 1 90px',
            minWidth: 0,
            lineHeight: 1.4,
            overflowWrap: 'normal',
            wordBreak: 'normal',
          }}
        >
          {footLeft}
        </span>
        {footRight}
      </div>
    </div>
  );
}
